import React, { useEffect, useRef } from 'react'
import { Switch, Route, Link, useParams, useLocation } from 'react-router-dom'
import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'

import Breadcrumbs from '@/Layout/Breadcrumbs'
import PublicProfile from '@/pages/Users/PublicProfile'
import Loading from '@/shared/Loading'

const CannotShowProfile = () => (
  <main className='container mx-auto max-w-xl px-4 sm:px-6 lg:px-8'>
    <div className='mt-10 rounded-md bg-yellow-50 p-4 text-yellow-900'>
      <div className='pb-3 text-sm'>This profile is too new to show publicly. It opens up as they get going.</div>
      <h3 className='text-sm font-medium text-yellow-800'>Search <Link to='/talent'>other talent</Link></h3>
    </div>
  </main>
)

function trackProfileViews (username) {
  const location = useLocation()
  useEffect(() => {
    if (typeof window.ga === 'function') {
      window.ga('send', { hitType: 'event', eventCategory: 'Profile', eventAction: 'view', eventLabel: username })
    }
  }, [location])
}

// The page title used to print the display name a second time above the
// header card. The header owns the name now, so this is breadcrumbs plus
// the page.
export default function ProfileContainer (props) {
  const { getRequest } = useQuery()
  const { username } = useParams()
  const topRef = useRef(null)
  const [profileContainer, setProfileContainer] = useGlobalState('profileContainer')

  trackProfileViews(username)

  useEffect(() => {
    getRequest(`/api/v3/users/${username}`, {}, (err, jsonData) => {
      if (err) { /* handled in hook */ return }
      setProfileContainer({
        ...profileContainer,
        user: jsonData.user,
        blocked: jsonData.blocked,
        blocking: jsonData.blocking,
        demos: jsonData.demos,
        genderAges: jsonData.genderAges,
        languages: jsonData.languages,
        skills: jsonData.skills,
        interests: jsonData.interests,
        accents: jsonData.accents,
        softwares: jsonData.softwares,
        hardwares: jsonData.hardwares,
        isFollowing: jsonData.isFollowing,
        userMeta: jsonData.userMeta,
        // First-paint sections; the tabs and "show all" fetch the rest.
        showcase: jsonData.showcase,
        educations: jsonData.educations,
        recommendationsReceived: jsonData.recommendationsReceived,
        recommendationsReceivedCount: jsonData.recommendationsReceivedCount,
        recommendationsGivenCount: jsonData.recommendationsGivenCount,
        userCredits: jsonData.userCredits,
        userCreditsCount: jsonData.userCreditsCount
      })
      topRef.current?.scrollIntoView()
    })
  }, [username])

  const { user } = profileContainer
  if (!user?.id || user.username?.toLowerCase() !== username.toLowerCase()) { return <Loading /> }
  if (!user.canShowPublicProfile) { return <CannotShowProfile /> }

  return <div ref={topRef}>
    <Breadcrumbs>
      <Link to='/talent' className='text-xs text-gray-500 hover:text-gray-700 dark:text-gray-100 sm:text-sm'>Members</Link>
      <Link to={`/m/${user.username}`} className='text-xs text-gray-500 hover:text-gray-700 dark:text-gray-100 sm:text-sm'>{user.displayName}</Link>
    </Breadcrumbs>
    <Switch>
      <Route {...props} exact path={['/m/:username']}>
        <PublicProfile />
      </Route>
    </Switch>
  </div>
}
